if (!customElements.get('anchor-navigation')) {
  customElements.define(
    'anchor-navigation',
    class AnchorNavigation extends HTMLElement {
      links = [];
      targets = [];
      sectionWrapper = null;
      header = null;
      frame = null;
      resizeObserver = null;
      clickedLink = null;
      clickedTarget = null;
      scrollSettleTimer = null;

      connectedCallback() {
        this.links = Array.from(this.querySelectorAll('[data-anchor-navigation-link]'));
        this.sectionWrapper = this.closest('.shopify-section');
        this.header = document.querySelector('.section-header');

        this.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange, { passive: true });
        document.addEventListener('shopify:block:select', this.handleBlockSelect);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleViewportChange);
          if (this.header) this.resizeObserver.observe(this.header);
          if (this.sectionWrapper) this.resizeObserver.observe(this.sectionWrapper);
        }

        this.refreshTargets();
        this.updateViewportState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('scroll', this.handleViewportChange);
        window.removeEventListener('resize', this.handleViewportChange);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.resizeObserver?.disconnect();
        document.documentElement.style.removeProperty('--anchor-navigation-sticky-offset');
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      }

      handleClick = (event) => {
        const link = event.target.closest('[data-anchor-navigation-link]');
        if (!link || !this.contains(link)) return;

        const target = this.resolveTarget(link.dataset.anchorTarget);
        if (!target) return;

        event.preventDefault();
        this.beginClickScroll(link, target);
        this.setActiveLink(link, true);
        this.scrollToTarget(target);
      };

      handleBlockSelect = (event) => {
        const item = event.target.closest('.anchor-navigation__item');
        const link = item?.querySelector('[data-anchor-navigation-link]');
        if (!link || !this.contains(link)) return;
        this.setActiveLink(link, true);
      };

      handleViewportChange = () => {
        if (this.clickedLink) this.scheduleClickScrollEnd();
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateViewportState();
        });
      };

      beginClickScroll(link, target) {
        this.clickedLink = link;
        this.clickedTarget = target;
        this.scheduleClickScrollEnd();
      }

      scheduleClickScrollEnd() {
        if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = window.setTimeout(this.finishClickScroll, 180);
      }

      finishClickScroll = () => {
        const target = this.clickedTarget;
        this.clickedLink = null;
        this.clickedTarget = null;
        this.scrollSettleTimer = null;

        if (target?.isConnected) {
          this.updateHeaderOffset();
          const correction = target.getBoundingClientRect().top - this.getScrollOffset();
          if (Math.abs(correction) > 2) {
            window.scrollBy({ top: correction, behavior: 'auto' });
          }
        }

        this.handleViewportChange();
      };

      updateViewportState() {
        this.updateHeaderOffset();
        this.refreshTargets();
        this.updateActiveLink();
      }

      updateHeaderOffset() {
        if (!this.sectionWrapper) return;
        const headerBottom = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
        const navigationHeight = this.sectionWrapper.offsetHeight || this.offsetHeight;
        this.sectionWrapper.style.setProperty('--anchor-navigation-top', `${Math.round(headerBottom)}px`);
        document.documentElement.style.setProperty(
          '--anchor-navigation-sticky-offset',
          `${Math.round(headerBottom + navigationHeight)}px`
        );
      }

      refreshTargets() {
        this.targets = this.links
          .map((link) => ({ link, target: this.resolveTarget(link.dataset.anchorTarget) }))
          .filter(({ target }) => target);
      }

      resolveTarget(rawTarget = '') {
        const targetName = rawTarget.trim();
        if (!targetName) return null;

        const normalized = targetName.replace(/^#/, '').toLowerCase();
        const aliases = {
          product: ['product-info[id^="MainProduct-"]', 'main product-info'],
          shop: ['product-info[id^="MainProduct-"]', 'main product-info'],
          details: [
            '[data-anchor-id="details"]',
            '#product-details',
            '.product__description',
            '[id$="__disclosures"]',
          ],
          compare: [
            '[data-anchor-id="compare"]',
            '#compare',
            '[id*="compare" i]',
            '.product-comparison',
            '[id$="__related-products"]',
            'product-recommendations',
          ],
          reviews: [
            '[data-anchor-id="reviews"]',
            '#reviews',
            '#shopify-product-reviews',
            '.jdgm-rev-widg',
            '.yotpo-main-widget',
            '[id*="reviews" i]',
          ],
          video: ['[data-anchor-id="video"]', '.product-video-reel'],
        };

        const selectors = aliases[normalized] ? [...aliases[normalized]] : [];
        if (/^[.#[]/.test(targetName)) {
          selectors.unshift(targetName);
        } else {
          const escapedTarget = this.escapeSelector(targetName.replace(/^#/, ''));
          selectors.unshift(`#${escapedTarget}`, `[data-anchor-id="${escapedTarget}"]`);
        }

        for (const selector of selectors) {
          try {
            const match = document.querySelector(selector);
            if (match && !this.contains(match)) return match;
          } catch (error) {
            console.warn(`Invalid anchor navigation selector: ${selector}`, error);
          }
        }

        const headingName = normalized.replace(/[-_]+/g, ' ');
        const heading = Array.from(document.querySelectorAll('main h1, main h2, main h3, main h4')).find(
          (element) => element.textContent.trim().toLowerCase() === headingName
        );
        return heading?.closest('.shopify-section') || heading || null;
      }

      escapeSelector(value) {
        if (window.CSS?.escape) return window.CSS.escape(value);
        return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      }

      scrollToTarget(target) {
        const offset = this.getScrollOffset();
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        window.scrollTo({ top, behavior });

        if (target.id) window.history.replaceState({}, '', `#${encodeURIComponent(target.id)}`);
      }

      getScrollOffset() {
        const headerOffset = parseFloat(
          this.sectionWrapper?.style.getPropertyValue('--anchor-navigation-top') || '0'
        );
        return headerOffset + (this.sectionWrapper?.offsetHeight || this.offsetHeight) + 12;
      }

      updateActiveLink() {
        if (!this.targets.length) return;

        if (this.clickedLink?.isConnected) {
          this.setActiveLink(this.clickedLink, false);
          return;
        }

        const threshold = window.scrollY + this.getScrollOffset() + 24;
        const orderedTargets = this.targets
          .map((item) => ({ ...item, top: item.target.getBoundingClientRect().top + window.scrollY }))
          .sort((a, b) => a.top - b.top);

        const atPageEnd =
          window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
        let active = atPageEnd ? orderedTargets[orderedTargets.length - 1] : orderedTargets[0];

        if (!atPageEnd) {
          orderedTargets.forEach((item) => {
            if (item.top <= threshold) active = item;
          });
        }

        this.setActiveLink(active.link, false);
      }

      setActiveLink(activeLink, centerLink) {
        this.links.forEach((link) => {
          const active = link === activeLink;
          link.classList.toggle('is-active', active);
          if (active) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
        });

        if (centerLink) this.centerActiveLink(activeLink);
      }

      centerActiveLink(activeLink) {
        const scroller = activeLink.closest('.anchor-navigation__inner');
        if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();
        const linkCenter = scroller.scrollLeft + linkRect.left - scrollerRect.left + linkRect.width / 2;
        const desiredLeft = linkCenter - scroller.clientWidth / 2;
        const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        scroller.scrollTo({
          left: Math.min(maxLeft, Math.max(0, desiredLeft)),
          behavior,
        });
      }
    }
  );
}
